import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, Save, Trash2, UserPlus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Field } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useBusinessProfile,
  useFamilyProfile,
  useUpdateBusinessProfile,
  useUpdateFamilyProfile,
  type BusinessProfile,
  type DependentChild,
  type EntityType,
  type FamilyProfile,
  type FilingStatus,
  type TaxTreatment,
} from "@/hooks/useProfile";

export function PeoplePage() {
  return (
    <>
      <PageHeader
        eyebrow="People"
        title="Owners, family, payroll candidates"
        description="Drives every recommendation: filing status, spouse income, kids, residency, prior-year tax."
      />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <BusinessProfileCard />
        <FamilyProfileCard />
      </div>
      <ChildrenCard />
    </>
  );
}

function BusinessProfileCard() {
  const profile = useBusinessProfile();
  const update = useUpdateBusinessProfile();
  const [draft, setDraft] = useState<BusinessProfile | null>(null);

  useEffect(() => {
    if (profile.data && !draft) setDraft(profile.data);
  }, [profile.data, draft]);

  if (profile.isLoading || !draft) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Business profile</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Business profile</CardTitle>
            <CardDescription>Legal form + filings tracking.</CardDescription>
          </div>
          <Button
            disabled={update.isPending}
            onClick={async () => {
              await update.mutateAsync(draft);
            }}
          >
            <Save className="h-4 w-4" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Legal name" className="sm:col-span-2">
          <Input
            value={draft.legalName ?? ""}
            onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
          />
        </Field>
        <Field label="Doing business as">
          <Input
            value={draft.doingBusinessAs ?? ""}
            onChange={(e) => setDraft({ ...draft, doingBusinessAs: e.target.value })}
          />
        </Field>
        <Field label="EIN">
          <Input
            value={draft.ein ?? ""}
            onChange={(e) => setDraft({ ...draft, ein: e.target.value })}
            placeholder="12-3456789"
          />
        </Field>
        <Field label="Entity type">
          <Select
            value={draft.entityType}
            onChange={(e) => setDraft({ ...draft, entityType: e.target.value as EntityType })}
          >
            {(["SoleProprietor", "SingleMemberLLC", "MultiMemberLLC", "SCorp", "CCorp", "Partnership"] as EntityType[]).map(
              (t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ),
            )}
          </Select>
        </Field>
        <Field label="Tax treatment">
          <Select
            value={draft.taxTreatment}
            onChange={(e) => setDraft({ ...draft, taxTreatment: e.target.value as TaxTreatment })}
          >
            {(["DisregardedScheduleC", "SCorp", "CCorp", "Partnership"] as TaxTreatment[]).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Formation date">
          <Input
            type="date"
            value={draft.formationDate ?? ""}
            onChange={(e) => setDraft({ ...draft, formationDate: e.target.value || null })}
          />
        </Field>
        <Field label="Formation state">
          <Input
            value={draft.formationState ?? ""}
            onChange={(e) => setDraft({ ...draft, formationState: e.target.value })}
            placeholder="WI"
            maxLength={2}
          />
        </Field>
        <Field label="State of operation">
          <Input
            value={draft.stateOfOperation ?? ""}
            onChange={(e) => setDraft({ ...draft, stateOfOperation: e.target.value })}
            placeholder="WI"
            maxLength={2}
          />
        </Field>
        <Field label="NAICS">
          <Input
            value={draft.naics ?? ""}
            onChange={(e) => setDraft({ ...draft, naics: e.target.value })}
            placeholder="541511"
          />
        </Field>
        <Field
          label="BOI status"
          hint="FinCEN's 3/21/2025 interim final rule exempted U.S.-formed entities from BOI reporting. Pick 'Handled by registered agent' if your formation service tracks compliance for you (CT Corp, Northwest, LegalZoom, etc.)."
        >
          <Select
            value={draft.boiStatus}
            onChange={(e) => setDraft({ ...draft, boiStatus: e.target.value as BusinessProfile["boiStatus"] })}
          >
            <option value="NotFiled">Not filed</option>
            <option value="HandledByAgent">Handled by registered agent</option>
            <option value="NotRequired">Not currently required (FinCEN 3/21/25)</option>
            <option value="Filed">Filed</option>
            <option value="Updated">Updated</option>
            <option value="Exempt">Exempt</option>
          </Select>
        </Field>
        <Field label="BOI confirmation #">
          <Input
            value={draft.boiConfirmationNumber ?? ""}
            onChange={(e) => setDraft({ ...draft, boiConfirmationNumber: e.target.value })}
          />
        </Field>
      </CardContent>
    </Card>
  );
}

function FamilyProfileCard() {
  const profile = useFamilyProfile();
  const update = useUpdateFamilyProfile();
  const [draft, setDraft] = useState<FamilyProfile | null>(null);

  useEffect(() => {
    if (profile.data && !draft) setDraft(profile.data);
  }, [profile.data, draft]);

  if (profile.isLoading || !draft) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Family profile</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Family profile</CardTitle>
            <CardDescription>Filing status, spouse, prior-year tax.</CardDescription>
          </div>
          <Button
            disabled={update.isPending}
            onClick={async () => {
              await update.mutateAsync(draft);
            }}
          >
            <Save className="h-4 w-4" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Filing status" className="sm:col-span-2">
          <Select
            value={draft.filingStatus ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                filingStatus: (e.target.value as FilingStatus) || null,
              })
            }
          >
            <option value="">— Select —</option>
            <option value="Single">Single</option>
            <option value="MarriedFilingJointly">Married filing jointly</option>
            <option value="MarriedFilingSeparately">Married filing separately</option>
            <option value="HeadOfHousehold">Head of household</option>
            <option value="QualifyingSurvivingSpouse">Qualifying surviving spouse</option>
          </Select>
        </Field>
        <Field label="Owner first name">
          <Input
            value={draft.ownerFirstName ?? ""}
            onChange={(e) => setDraft({ ...draft, ownerFirstName: e.target.value })}
          />
        </Field>
        <Field label="Owner last name">
          <Input
            value={draft.ownerLastName ?? ""}
            onChange={(e) => setDraft({ ...draft, ownerLastName: e.target.value })}
          />
        </Field>
        <Field label="State of residence">
          <Input
            value={draft.stateOfResidence ?? ""}
            maxLength={2}
            placeholder="WI"
            onChange={(e) =>
              setDraft({ ...draft, stateOfResidence: e.target.value || null })
            }
          />
        </Field>
        <Field label="Has spouse">
          <Select
            value={draft.hasSpouse ? "yes" : "no"}
            onChange={(e) => setDraft({ ...draft, hasSpouse: e.target.value === "yes" })}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Select>
        </Field>
        {draft.hasSpouse && (
          <>
            <Field label="Spouse first name">
              <Input
                value={draft.spouseFirstName ?? ""}
                onChange={(e) => setDraft({ ...draft, spouseFirstName: e.target.value })}
              />
            </Field>
            <Field label="Spouse last name">
              <Input
                value={draft.spouseLastName ?? ""}
                onChange={(e) => setDraft({ ...draft, spouseLastName: e.target.value })}
              />
            </Field>
          </>
        )}
        <div className="sm:col-span-2 rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
          W-2 wages (yours and spouse&apos;s) now live in{" "}
          <Link to="/tax-intake" className="text-foreground underline">
            Tax Intake
          </Link>
          . Per-employer YTD entries replace the old single annual number so the engine
          can project Social-Security base usage for SE tax.
        </div>
        <Field label="Prior-year AGI" hint="From Form 1040 line 11">
          <Input
            type="number"
            inputMode="decimal"
            value={draft.priorYearAgi ?? ""}
            onChange={(e) =>
              setDraft({ ...draft, priorYearAgi: e.target.value === "" ? null : Number(e.target.value) })
            }
          />
        </Field>
        <Field label="Prior-year total tax" hint="Form 1040 line 24">
          <Input
            type="number"
            inputMode="decimal"
            value={draft.priorYearTotalTax ?? ""}
            onChange={(e) =>
              setDraft({ ...draft, priorYearTotalTax: e.target.value === "" ? null : Number(e.target.value) })
            }
          />
        </Field>
        <Field label="HSA eligible (HDHP)">
          <Select
            value={draft.isHsaEligible ? "yes" : "no"}
            onChange={(e) => setDraft({ ...draft, isHsaEligible: e.target.value === "yes" })}
          >
            <option value="no">Not yet</option>
            <option value="yes">Yes</option>
          </Select>
        </Field>
      </CardContent>
    </Card>
  );
}

function ChildrenCard() {
  const profile = useFamilyProfile();
  const update = useUpdateFamilyProfile();

  const family = profile.data;

  const addChild = async () => {
    if (!family) return;
    const blank: DependentChild = {
      id: crypto.randomUUID(),
      ownerUserId: family.ownerUserId,
      familyProfileId: family.id,
      firstName: "Child",
      lastName: family.ownerLastName ?? null,
      dateOfBirth: new Date(new Date().getFullYear() - 8, 0, 1).toISOString().slice(0, 10),
      isOnPayroll: false,
      payrollStartDate: null,
      roleDescription: null,
      custodialRothIraProvider: null,
      custodialRothIraYtdContribution: null,
      edvest529AccountNumberMasked: null,
      edvest529YtdContribution: null,
    };
    await update.mutateAsync({ ...family, children: [...family.children, blank] });
  };

  const removeChild = async (id: string) => {
    if (!family) return;
    await update.mutateAsync({
      ...family,
      children: family.children.filter((c) => c.id !== id),
    });
  };

  const patchChild = async (id: string, patch: Partial<DependentChild>) => {
    if (!family) return;
    await update.mutateAsync({
      ...family,
      children: family.children.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Children {family?.children?.length != null && <Badge tone="info">{family.children.length}</Badge>}
            </CardTitle>
            <CardDescription>
              Hire-the-kids strategy: each child up to standard deduction (~$15k) is federally
              tax-free wages, and FICA-exempt while the parent owns a sole-prop or FMLLC.
            </CardDescription>
          </div>
          <Button onClick={addChild} disabled={!family || update.isPending}>
            <UserPlus className="h-4 w-4" />
            Add child
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {profile.isLoading && <Skeleton className="h-24 w-full" />}
        {family?.children.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No children entered. Add them so the advisor can model FICA-exempt wages and Edvest 529
            deductions.
          </p>
        )}
        {family?.children.map((c) => (
          <div
            key={c.id}
            className="grid grid-cols-1 gap-3 rounded-md border border-border/60 p-3 sm:grid-cols-6"
          >
            <Field label="First name" className="sm:col-span-2">
              <Input
                value={c.firstName}
                onChange={(e) => patchChild(c.id, { firstName: e.target.value })}
              />
            </Field>
            <Field label="DOB" className="sm:col-span-2">
              <Input
                type="date"
                value={c.dateOfBirth}
                onChange={(e) => patchChild(c.id, { dateOfBirth: e.target.value })}
              />
            </Field>
            <Field label="On payroll">
              <Select
                value={c.isOnPayroll ? "yes" : "no"}
                onChange={(e) => patchChild(c.id, { isOnPayroll: e.target.value === "yes" })}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </Select>
            </Field>
            <div className="flex items-end justify-end">
              <Button variant="ghost" size="icon" onClick={() => removeChild(c.id)} aria-label="Remove">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <Field label="Role" className="sm:col-span-3">
              <Input
                value={c.roleDescription ?? ""}
                onChange={(e) => patchChild(c.id, { roleDescription: e.target.value })}
                placeholder="e.g. Marketing model, filing assistant"
              />
            </Field>
            <Field label="Roth IRA provider" className="sm:col-span-3">
              <Input
                value={c.custodialRothIraProvider ?? ""}
                onChange={(e) => patchChild(c.id, { custodialRothIraProvider: e.target.value })}
                placeholder="Fidelity / Schwab / etc."
              />
            </Field>
          </div>
        ))}
        {family?.children.length !== 0 && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Plus className="h-3 w-3" /> Changes save immediately on the existing rows; use Add
            child for new entries.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
